'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useSession } from 'next-auth/react';
import { SCENARIO_ENGINE_KEY, SCENARIO_ENGINE_DEFAULT, isV2ScenarioEngine } from '@/lib/scenarioEngineControl';

interface ConfigValue {
  key: string;
  value: string | boolean | number | object;
  enabled: boolean;
  data_type: string;
  from_category: string | null;
  allowed_users: string | null;
}

interface AppConfigContextType {
  configs: Record<string, ConfigValue>;
  isLoading: boolean;
  isEnabled: (key: string) => boolean;
  getValue: <T = unknown>(key: string, defaultValue?: T) => T;
  refresh: (category?: string) => Promise<void>;
}

const AppConfigContext = createContext<AppConfigContextType | null>(null);

// All config keys that need to be loaded for frontend use
// Add new keys here as needed
const FRONTEND_CONFIG_KEYS = [
  // Module-level controls
  'module/walkforward_status',
  'module/loyalty_offers_status',
  'module/issues_tracking_status',
  // Feature-level controls
  'feature/issue_reporting_status',
  'feature/app_config_admin_status',
  'feature/admin_reported_issues_tracking_status',
  'feature/allow_past_scenario_alignment',
  'feature/allow_past_loyalty_offer_actions',
  // Table selection copy control (multiselect — see useCopyOptions)
  'feature/table_selection_copy_control',
];

interface AppConfigProviderProps {
  children: ReactNode;
  scope?: 'pace' | 'netpe';
}

export function AppConfigProvider({ children, scope = 'pace' }: AppConfigProviderProps) {
  const [configs, setConfigs] = useState<Record<string, ConfigValue>>({});
  const [isLoading, setIsLoading] = useState(true);
  const hasFetchedRef = useRef(false);
  const scopeRef = useRef(scope);
  let userEmail: string | null = null;
  let sessionStatus: 'authenticated' | 'unauthenticated' | 'loading' | undefined;
  try {
    const session = useSession();
    sessionStatus = session?.status;
    userEmail = session?.data?.user?.email?.toLowerCase() || null;
  } catch { /* session provider not available */ }

  // Defer the batch fetch until the user is actually signed in. The provider
  // is mounted in the root layout (wrapping /signin too); without this gate
  // every signin page load fires a wasted /api/app-config/batch round-trip.
  // When the session transitions unauthenticated -> authenticated, this
  // effect re-runs and performs the one-time fetch.
  useEffect(() => {
    if (sessionStatus !== 'authenticated') {
      // Not signed in yet (or session loading) — keep isLoading=true so any
      // gated UI shows a loading state rather than a flash of off-by-default
      // features. Setting it explicitly here in case a sign-out flips us
      // back to unauthenticated.
      setIsLoading(true);
      return;
    }
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchConfigs = async () => {
      try {
        // POST + JSON body — the frontend key list grew past comfortable URL
        // length and there's no caching to lose (route is force-dynamic).
        const response = await fetch('/api/app-config/batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            keys: FRONTEND_CONFIG_KEYS,
            scope: scopeRef.current,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          setConfigs(data.configs || {});
        }
      } catch (error) {
        console.error('Error fetching app configs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigs();
  }, [sessionStatus]);

  const isEnabled = useCallback(
    (key: string): boolean => {
      const config = configs[key];
      if (!config?.enabled) return false;
      // If allowed_users is set, check if current user is in the list
      if (config.allowed_users) {
        if (!userEmail) return false;
        const allowed = config.allowed_users.split(',').map(e => e.trim().toLowerCase());
        return allowed.includes(userEmail);
      }
      return true;
    },
    [configs, userEmail]
  );

  const getValue = useCallback(
    <T = unknown,>(key: string, defaultValue?: T): T => {
      const config = configs[key];
      if (!config) {
        return defaultValue as T;
      }
      return config.value as T;
    },
    [configs]
  );

  // Manual refresh function - can optionally pass category for category-specific refresh
  const refresh = useCallback(async (category?: string) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/app-config/batch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keys: FRONTEND_CONFIG_KEYS,
          scope: scopeRef.current,
          ...(category ? { category } : {}),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setConfigs(data.configs || {});
      }
    } catch (error) {
      console.error('Error refreshing app configs:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <AppConfigContext.Provider value={{ configs, isLoading, isEnabled, getValue, refresh }}>
      {children}
    </AppConfigContext.Provider>
  );
}

/**
 * Hook to access app configuration values.
 *
 * @example
 * const { isEnabled, getValue, isLoading } = useAppConfig();
 *
 * // Check if a module/feature is enabled
 * if (isEnabled('module/walkforward_status')) {
 *   // Show walkforward link
 * }
 *
 * // Get a config value with default
 * const timeout = getValue<number>('config/timeout', 5000);
 */
export function useAppConfig(): AppConfigContextType {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
}

/**
 * Hook to check if a specific feature/module is enabled.
 * Convenience wrapper for simple boolean checks.
 *
 * @example
 * const isWalkforwardEnabled = useFeatureEnabled('module/walkforward_status');
 */
export function useFeatureEnabled(key: string): boolean {
  const { isEnabled, isLoading } = useAppConfig();
  // Return false while loading to prevent flicker
  if (isLoading) return false;
  return isEnabled(key);
}

/**
 * Bulk scenario refresh is available only on a v2 generation engine (the batch flow).
 * Driven by the Scenario Generation Workflow selector — not a standalone flag.
 */
export function useBulkRefreshEnabled(): boolean {
  const { getValue, isLoading } = useAppConfig();
  if (isLoading) return false;
  return isV2ScenarioEngine(getValue<string>(SCENARIO_ENGINE_KEY, SCENARIO_ENGINE_DEFAULT));
}

/**
 * Hook to check multiple features at once.
 *
 * @example
 * const enabledMap = useFeaturesEnabled([
 *   'module/walkforward_status',
 *   'module/loyalty_offers_status'
 * ]);
 * // enabledMap = { 'module/walkforward_status': true, 'module/loyalty_offers_status': false }
 */
export function useFeaturesEnabled(keys: string[]): Record<string, boolean> {
  const { isEnabled, isLoading } = useAppConfig();

  if (isLoading) {
    // Return all false while loading
    return keys.reduce(
      (acc, key) => {
        acc[key] = false;
        return acc;
      },
      {} as Record<string, boolean>
    );
  }

  return keys.reduce(
    (acc, key) => {
      acc[key] = isEnabled(key);
      return acc;
    },
    {} as Record<string, boolean>
  );
}
